import { useApolloClient } from "@apollo/client";
import {
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  ListHeader,
  Modal,
  ModalOpenButton,
  Select,
  SelectMultiple,
  useRunWithFlag,
} from "@msys/ui";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EditIcon from "@mui/icons-material/Edit";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { isEqual, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  namedOperations,
  OrganisationMembershipsFilters,
  OrganisationMembershipsSortBy,
  OrganisationMembershipsSorting,
  OrganisationMembershipStatus,
  Salutation,
  SortDirection,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { ButtonCreate } from "../../commons/button/Button.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { FilterChipGroup } from "../../commons/filters/FilterChip.js";
import { FilterModal } from "../../commons/filters/FilterModal.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import { SelectMultipleField } from "../../commons/form-fields/SelectMultipleField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../constants.js";
import { CancelOrganisationMembershipInvitationButton } from "../../features/invitations/CancelOrganisationMembershipInvitationButton.js";
import {
  InviteOrganisationMemberModal,
  OrganisationMembershipInvitationEmailFormValues,
} from "../../features/invitations/InviteOrganisationMemberModal.js";
import { ResendOrganisationMembershipInvitationEmailModal } from "../../features/invitations/ResendOrganisationMembershipInvitationEmailModal.js";
import { useOrganisationMembershipInvitation } from "../../features/invitations/useOrganisationMembershipInvitation.js";
import { DeactivateUserButton } from "../../features/users/DeactivateUserButton.js";
import { getMemberOptions } from "../../features/users/helpers.js";
import { OrganisationMembershipStatusLabel } from "../../features/users/OrganisationMembershipStatusLabel.js";
import { ReactivateUserButton } from "../../features/users/ReactivateUserButton.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useOrganisationMembershipStatuses } from "../../features/users/useOrganisationMembershipStatuses.js";
import { UserAvatar } from "../../features/users/UserAvatar.js";
import {
  useOrganisationRoles,
  useProjectRoles,
} from "../../features/users/useRoles.js";
import { UserTitleWithNameField } from "../../features/users/UserTitleWithNameField.js";
import {
  OrganisationMemberRowFragment,
  useOrganisationMembers_CreateNewOrganisationMemberMutation,
  useOrganisationMembers_MembershipsQuery,
  useOrganisationMembers_OrganisationQuery,
  useOrganisationMembers_SetMemberAsOrganisationRepresentativeMutation,
} from "./OrganisationMembers.generated";

const DEFAULT_SORTING: OrganisationMembershipsSorting[] = [
  { column: "firstname", direction: "asc" },
];

interface Props {
  submenuItems: PageTopbarItem[];
}

export function OrganisationMembers({ submenuItems }: Props) {
  const navigate = useNavigate();
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate([
    "OrganisationInvite",
    "OrganisationProfile",
    "UserProfile",
    "Global",
  ]);
  const { getOrganisationRoleTitle, getOrganisationRoleOptions } =
    useOrganisationRoles();
  const { statusOptions, statusLabels } = useOrganisationMembershipStatuses();

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters: { searchTerm, ...filters },
    setFilters,
    resetFilters,
    sorting,
    setSorting,
  } = useFiltersAndPagination<
    OrganisationMembershipsSorting,
    OrganisationMembershipsFilters & { searchTerm?: string }
  >({}, DEFAULT_SORTING, PAGE_LIST_RESULTS_PER_PAGE);

  const client = useApolloClient();
  const organisationQuery = useOrganisationMembers_OrganisationQuery({
    client,
  });
  const membershipsQuery = useOrganisationMembers_MembershipsQuery({
    client,
    variables: {
      offset,
      limit,
      sorting,
      filters,
      searchTerm,
    },
  });

  const organisation = organisationQuery.data?.viewer?.organisation;
  const organisationRoles = organisation?.roles ?? [];
  const members =
    (
      membershipsQuery.data ?? membershipsQuery.previousData
    )?.organisationMemberships.edges.map(edge => edge.node) ?? [];
  const totalCount =
    (membershipsQuery.data ?? membershipsQuery.previousData)
      ?.organisationMemberships.totalCount ?? 0;

  const stateStore = useDataGridStateStore("organisation-members");

  const theme = useTheme();

  const columns = React.useMemo(
    (): GridColDef<OrganisationMemberRowFragment>[] => [
      {
        field: "firstname",
        headerName: t("Name", { ns: "Global" }),
        flex: 1,
        renderCell: ({ row }) => {
          const elements: React.ReactNode[] = [
            <UserAvatar key="avatar" userAvatar={row} size="s" />,
            <Typography key="name">{row.fullname}</Typography>,
          ];

          if (row.id === viewer.id) {
            elements.push(
              <Typography key="me">
                {`(${t("me", {
                  ns: "Global",
                })})`}
              </Typography>
            );
          }

          return (
            <Stack direction="row" alignItems={"center"} spacing={1}>
              {elements}
            </Stack>
          );
        },
      },
      {
        field: "jobTitle",
        headerName: t("Job title", { ns: "UserProfile" }),
        flex: 1,
        sortable: false,
        valueGetter: (value, row) => {
          return row.jobTitle ?? "-";
        },
      },
      {
        field: "roles",
        headerName: t("Roles", {
          ns: "OrganisationProfile",
        }),
        flex: 1,
        sortable: false,
        valueGetter: (value, row) => {
          const roles = getOrganisationRoleTitle(row.roles);

          return roles;
        },
      },
      {
        field: "email",
        headerName: t("Email", { ns: "Global" }),
        flex: 1,
        sortable: false,
        valueGetter: (value, row) => row.email,
      },
      {
        field: "phone",
        headerName: t("Phone", { ns: "Global" }),
        flex: 1,
        sortable: false,
        valueGetter: (value, row) =>
          row.__typename === "User" ? row.phones[0]?.number : "-",
      },
      {
        field: "organisationMembershipStatus",
        headerName: t("Status", { ns: "Global" }),
        flex: 1,
        valueGetter: (value, row) => row.membershipStatus,
        renderCell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {row.id === organisation?.representive?.id && (
                <Tooltip
                  key="representive-icon"
                  title={t("Organisation representative", {
                    ns: "OrganisationProfile",
                  })}
                >
                  <AccountCircleIcon />
                </Tooltip>
              )}
              <OrganisationMembershipStatusLabel
                membershipStatus={row.membershipStatus}
                invitation={row.membershipInvitation ?? null}
              />
            </Stack>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        flex: 3,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            {row.phones.length > 0 && (
              <IconButton
                key="phone-button"
                size="small"
                color="primary"
                component="a"
                href={`tel:${row.phones[0].number}`}
              >
                <PhoneIcon />
              </IconButton>
            )}
            {row.membershipStatus === "UNACTIVATED" &&
              (row.membershipInvitation ? (
                <>
                  <ModalOpenButton
                    Modal={ResendOrganisationMembershipInvitationEmailModal}
                    modalProps={{
                      invitationId: row.membershipInvitation.id,
                      refetchQueries: [
                        namedOperations.Query.OrganisationMembers_Memberships,
                      ],
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                      }}
                    >
                      {t("Resend invitation", {
                        ns: "OrganisationInvite",
                      })}
                    </Button>
                  </ModalOpenButton>
                  <CancelOrganisationMembershipInvitationButton
                    invitationId={row.membershipInvitation.id}
                    refetchQueries={[
                      namedOperations.Query.OrganisationMembers_Memberships,
                    ]}
                    size="small"
                  />
                </>
              ) : (
                <ModalOpenButton
                  Modal={InviteOrganisationMemberModal}
                  modalProps={{
                    userId: row.id,
                    refetchQueries: [
                      namedOperations.Query.OrganisationMembers_Memberships,
                    ],
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                  >
                    {t("Invite", { ns: "Global" })}
                  </Button>
                </ModalOpenButton>
              ))}
            {row.membershipStatus === "DEACTIVATED" && (
              <ReactivateUserButton userId={row.id}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                >
                  {t("Reactivate", { ns: "UserProfile" })}
                </Button>
              </ReactivateUserButton>
            )}
            {row.membershipStatus === "ACTIVATED" && (
              <DeactivateUserButton
                userId={row.id}
                refetchQueries={[
                  namedOperations.Query.OrganisationMembers_Memberships,
                ]}
                disabled={
                  row.id === viewer.id ||
                  row.id === organisation?.representive?.id
                }
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                >
                  {t("Deactivate", { ns: "UserProfile" })}
                </Button>
              </DeactivateUserButton>
            )}
          </Stack>
        ),
      },
    ],
    [getOrganisationRoleTitle, organisation?.representive?.id, t, viewer.id]
  );

  return (
    <Page title={organisation?.title} submenuItems={submenuItems}>
      {organisation && (
        <PageContainer>
          <Stack spacing={2}>
            <ListHeader
              title={t("Members", { ns: "OrganisationProfile" })}
              FilterButton={
                <ModalOpenButton
                  Modal={OrganisationMembersFilterModal}
                  modalProps={{
                    sorting,
                    setSorting,
                    filters,
                    setFilters,
                    resetFilters,
                    statusOptions,
                    roleOptions: getOrganisationRoleOptions(organisationRoles),
                  }}
                >
                  <FilterButton />
                </ModalOpenButton>
              }
              FilterChips={
                <Stack direction={"row"} spacing={1} alignItems={"center"}>
                  <FilterChipGroup
                    label={t("Status", {
                      ns: "Global",
                    })}
                    values={filters.statuses}
                    getValueLabel={value => statusLabels[value]}
                    setValues={value => {
                      setFilters(state => ({
                        ...state,
                        statuses: value,
                      }));
                    }}
                  />
                  <FilterChipGroup
                    label={t("Role", {
                      ns: "Global",
                    })}
                    values={filters.roleIds}
                    getValueLabel={value => {
                      const role = organisationRoles.find(r => r.id === value);

                      return role ? getOrganisationRoleTitle([role]) : "?";
                    }}
                    setValues={value => {
                      setFilters(state => ({
                        ...state,
                        roleIds: value,
                      }));
                    }}
                  />
                  {((filters.statuses && filters.statuses.length > 0) ||
                    (filters.roleIds && filters.roleIds.length > 0)) && (
                    <Typography variant="body2">
                      <Link
                        onClick={() => {
                          setFilters(state => ({
                            ...state,
                            roleIds: undefined,
                            statuses: undefined,
                          }));
                        }}
                        color="secondary"
                        sx={{ cursor: "pointer" }}
                      >
                        {t("Clear all", { ns: "Global" })}
                      </Link>
                    </Typography>
                  )}
                </Stack>
              }
              SearchField={
                <DebouncedSearchInput
                  defaultValue={searchTerm}
                  placeholder={t("Search", { ns: "Global" })}
                  onChangeSearchTerm={newSearchTerm => {
                    setFilters(state => ({
                      ...state,
                      searchTerm: newSearchTerm,
                    }));
                  }}
                  autoFocus
                  style={{
                    flexGrow: 1,
                    maxWidth: `${theme.layout.searchFieldWidth.lg}px`,
                  }}
                />
              }
              ActionButtons={
                <>
                  <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                    {viewer.organisation.id === organisation.id &&
                      !!viewer.roles.find(
                        r => r.internalName === "ORG_ADMIN"
                      ) && (
                        <ModalOpenButton
                          Modal={ChangeRepresentativeModal}
                          modalProps={{
                            organisationId: organisation.id,
                            activeMembers: members.filter(
                              member =>
                                member.membershipStatus !== "DEACTIVATED"
                            ),
                            organisationRepresentativeId:
                              organisation.representive?.id,
                          }}
                        >
                          <ButtonCreate
                            Icon={<EditIcon />}
                            variant="text"
                            title={t("Change representative", {
                              ns: "OrganisationProfile",
                            })}
                          />
                        </ModalOpenButton>
                      )}
                  </RestrictedByOrganisationPermissionWithDebug>
                  <RestrictedByOrganisationPermissionWithDebug permission="ADD_MEMBER_TO_ORG">
                    <ModalOpenButton
                      Modal={CreateNewUserModal}
                      modalProps={{
                        refetchQueries: [
                          namedOperations.Query.OrganisationMembers_Memberships,
                        ],
                      }}
                    >
                      <ButtonCreate
                        title={t("Add new member", {
                          ns: "OrganisationProfile",
                        })}
                        sx={{ flexShrink: 0, flexGrow: 0 }}
                      />
                    </ModalOpenButton>
                  </RestrictedByOrganisationPermissionWithDebug>
                </>
              }
            />
            <DataGrid
              columns={columns}
              rows={members}
              rowCount={totalCount}
              onRowClick={({ row }) => {
                if (row.__typename === "User") {
                  navigate(`/organisation/users/${row.id}`);
                }
              }}
              density="standard"
              stateStore={stateStore}
              paginationMode={"server"}
              paginationModel={paginationModel}
              onPaginationModelChange={model => {
                setPaginationModel(model);
              }}
              sortModel={sorting.map(s => ({
                field: s.column,
                sort: s.direction,
              }))}
              onSortModelChange={newModel => {
                setSorting(
                  newModel.map(({ field, sort }) => ({
                    column: field as OrganisationMembershipsSortBy,
                    direction: sort ?? "asc",
                  }))
                );
              }}
              loading={membershipsQuery.loading}
            />
          </Stack>
        </PageContainer>
      )}
    </Page>
  );
}

const ChangeRepresentativeModal = ({
  organisationId,
  activeMembers,
  organisationRepresentativeId,
  handleClose,
}: {
  organisationId: string;
  activeMembers: Parameters<typeof getMemberOptions>[0];
  organisationRepresentativeId: string | undefined | null;
  handleClose: () => void;
}) => {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const [representativeId, setRepresentativeId] = React.useState<string | null>(
    organisationRepresentativeId ?? null
  );

  const client = useApolloClient();
  const [modifyOrganisation] =
    useOrganisationMembers_SetMemberAsOrganisationRepresentativeMutation({
      client,
    });

  const [processing, run] = useRunWithFlag();

  const handleSubmit = async (representativeId: string) => {
    try {
      await modifyOrganisation({
        variables: {
          organisationId,
          userId: representativeId,
        },
        refetchQueries: [namedOperations.Query.OrganisationProfile],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  return (
    <Modal
      title="Change representative"
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: processing },
        },
        {
          label: t("Confirm", { ns: "Global" }),
          buttonProps: {
            loading: processing,
            disabled: !representativeId,
          },
          handleClick: async () => {
            if (!representativeId) {
              throw new Error("Representative is required");
            }
            await run(async () => {
              await handleSubmit(representativeId);
              handleClose();
            });
          },
        },
      ]}
    >
      <Select
        label={t("Organisation representative", {
          ns: "OrganisationProfile",
        })}
        options={getMemberOptions(activeMembers)}
        value={representativeId}
        onChange={value => {
          setRepresentativeId(value);
        }}
      />
    </Modal>
  );
};

interface CreateNewUserFormValues
  extends OrganisationMembershipInvitationEmailFormValues {
  title: Salutation;
  firstName: string;
  familyName: string;
  email: string;
  jobTitle: string;
  roleIds: string[];
  defaultProjectRoleId?: string;
  inviteToUseMeisterSystems: boolean;
}

const CreateNewUserModal = ({
  handleClose,
  handleComplete,
  refetchQueries,
}: {
  handleClose: () => void;
  handleComplete?: () => Promise<void>;
  refetchQueries: string[];
}) => {
  const { t } = useTranslate(["OrganisationInvite", "UserProfile", "Global"]);
  const viewer = useUserData().currentUser!;
  const { enqueueSnackbar } = useSnackbar();

  const {
    initialValues: invitationInitialValues,
    validationSchema: invitationValidationSchema,
    formFields,
    createSubmitHandler: createInviteHandler,
    isLoading: isInviting,
  } = useOrganisationMembershipInvitation({ refetchQueries });

  const client = useApolloClient();
  const [create, { loading: isLoading }] =
    useOrganisationMembers_CreateNewOrganisationMemberMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });

  const onSubmit = React.useCallback(
    async (values: CreateNewUserFormValues) => {
      const result = await create({
        variables: {
          input: {
            user: {
              salutation: values.title,
              firstName: values.firstName,
              lastName: values.familyName,
              email: values.email,
              jobTitle: values.jobTitle,
              organisationRoleIds: values.roleIds,
              defaultProjectRoleIds: values.defaultProjectRoleId
                ? [values.defaultProjectRoleId]
                : [],
            },
          },
        },
      });

      const user = result.data?.createSystemUser?.user;

      if (!user) {
        throw new Error("User creation failed");
      }

      if (values.inviteToUseMeisterSystems) {
        const handleInvite = createInviteHandler(user.id);
        await handleInvite({
          message: values.message,
        });
      }

      handleClose();
      await handleComplete?.();
      enqueueSnackbar(
        t("Member invited", {
          ns: "OrganisationInvite",
        })
      );
    },
    [
      create,
      handleClose,
      handleComplete,
      enqueueSnackbar,
      t,
      createInviteHandler,
    ]
  );

  const { getOrganisationRoleOptions } = useOrganisationRoles();
  const { getProjectRoleOptions } = useProjectRoles();

  const roles = getOrganisationRoleOptions(viewer.organisation.roles);
  const projectRoles = getProjectRoleOptions(viewer.organisation.projectRoles);

  const defaultRoleId = viewer.organisation.roles.find(r =>
    r.internalName.endsWith("_MEMBER")
  )?.id;
  const defaultProjectRoleId = viewer.organisation.projectRoles.find(r =>
    r.internalName.endsWith("_MEMBER")
  )?.id;

  const initialValues: CreateNewUserFormValues = {
    title: "mr",
    jobTitle: "",
    firstName: "",
    familyName: "",
    email: "",
    roleIds: defaultRoleId ? [defaultRoleId] : [],
    defaultProjectRoleId,
    inviteToUseMeisterSystems: true,
    ...invitationInitialValues,
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        title: Yup.string()
          .label(
            t("Salutation", {
              ns: "Global",
            })
          )
          .required(),
        firstName: Yup.string().label(
          t("First name", {
            ns: "UserProfile",
          })
        ),
        familyName: Yup.string()
          .label(
            t("Last name", {
              ns: "UserProfile",
            })
          )
          .required(),
        email: Yup.string()
          .label(
            t("Email", {
              ns: "OrganisationInvite",
            })
          )
          .trim()
          .email()
          .required(),
        jobTitle: Yup.string().label(
          t("Job title", {
            ns: "UserProfile",
          })
        ),
        roleIds: Yup.array()
          .label(
            t("Organisation role", {
              ns: "UserProfile",
            })
          )
          .of(Yup.string())
          .min(1)
          .required(
            t("At least one organisation role is required", {
              ns: "UserProfile",
            })
          ),
        defaultProjectRoleId: Yup.string()
          .label(
            t("Default project role", {
              ns: "UserProfile",
            })
          )
          .required(
            t("Default project role is required", {
              ns: "UserProfile",
            })
          ),
        inviteToUseMeisterSystems: Yup.boolean().label(
          t("Invite to use MeisterSystems", {
            ns: "OrganisationInvite",
          })
        ),
        message: invitationValidationSchema.fields.message.when(
          "inviteToUseMeisterSystems",
          {
            is: true,
            then: invitationValidationSchema.fields.message.required(),
            otherwise: invitationValidationSchema.fields.message.notRequired(),
          }
        ),
      }),
    [invitationValidationSchema.fields, t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<CreateNewUserFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {formikProps => (
        <Modal
          title={t("Create new member", { ns: "OrganisationInvite" })}
          handleClose={handleClose}
          isLoading={isLoading}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Confirm", {
                ns: "Global",
              }),
              buttonProps: {
                type: "submit",
                form: formId,
                disabled: isLoading || isInviting,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack flexDirection="column" spacing={1}>
              <Box>
                <UserTitleWithNameField />
              </Box>
              <TextField
                label={t("Email", {
                  ns: "OrganisationInvite",
                })}
                name="email"
                type="email"
                required
              />
              <SelectMultipleField
                label={t("Organisation role", {
                  ns: "UserProfile",
                })}
                name="roleIds"
                options={roles}
                required
                // FIXME
                // validate={value => {
                //   let message;
                //   if (value.length === 0) {
                //     message = t("At least one organisation role is required", {
                //       ns: "UserProfile",
                //     });
                //   }
                //   return message;
                // }}
                disabled={false}
              />
              <TextField
                name="jobTitle"
                label={t("Job title", {
                  ns: "UserProfile",
                })}
                disabled={false}
              />
              <SelectField
                label={t("Default project role", {
                  ns: "UserProfile",
                })}
                name="defaultProjectRoleId"
                options={projectRoles}
                disabled={false}
              />
              <CheckboxField
                name="inviteToUseMeisterSystems"
                label={t("Invite to use MeisterSystems", {
                  ns: "OrganisationInvite",
                })}
              />
              {formikProps.values.inviteToUseMeisterSystems && formFields}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

const OrganisationMembersFilterModal = ({
  handleClose,
  sorting,
  setSorting,
  filters,
  setFilters,
  resetFilters,
  statusOptions,
  roleOptions,
}: {
  handleClose: () => void;
  sorting: OrganisationMembershipsSorting[];
  setSorting: (sorting: OrganisationMembershipsSorting[]) => void;
  filters: OrganisationMembershipsFilters;
  setFilters: React.Dispatch<
    React.SetStateAction<OrganisationMembershipsFilters>
  >;
  resetFilters: () => void;
  statusOptions: {
    label: string;
    value: OrganisationMembershipStatus;
  }[];
  roleOptions: {
    label: string;
    value: string;
  }[];
}) => {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  const [filtersState, setFiltersState] = React.useState(filters);
  const [sortingState, setSortingState] = React.useState(sorting);

  console.log("filtersState", filtersState);

  return (
    <FilterModal
      title={t("Organisation members filters", {
        ns: "OrganisationProfile",
      })}
      handleClose={handleClose}
      handleApply={() => {
        setSorting(sortingState);
        setFilters(filtersState);
      }}
      handleReset={() => resetFilters()}
      spacing={1}
    >
      <OrganisationMembersSorting
        defaultSorting={DEFAULT_SORTING}
        sorting={sortingState[0]}
        setSorting={setSortingState}
      />
      <SelectMultiple
        label={t("Status", { ns: "Global" })}
        options={statusOptions}
        value={filtersState.statuses ?? []}
        onChange={value => {
          setFiltersState(state => ({
            ...state,
            statuses: value,
          }));
        }}
      />
      <SelectMultiple
        label={t("Role", { ns: "OrganisationProfile" })}
        options={roleOptions}
        value={filtersState.roleIds ?? []}
        onChange={value => {
          setFiltersState(state => ({
            ...state,
            roleIds: value,
          }));
        }}
      />
    </FilterModal>
  );
};

type SortBy = Exclude<
  OrganisationMembershipsSortBy,
  "distanceFromHomeAddressToLocation"
>;

const ORGANISATION_MEMBERS_SORTING_FIELDS: {
  column: SortBy;
  direction: SortDirection;
}[] = [
  { column: "createdAt", direction: "desc" },
  { column: "createdAt", direction: "asc" },
  { column: "firstname", direction: "desc" },
  { column: "firstname", direction: "asc" },
  { column: "familyname", direction: "desc" },
  { column: "familyname", direction: "asc" },
  { column: "organisationMembershipStatus", direction: "asc" },
  { column: "organisationMembershipStatus", direction: "desc" },
];

const OrganisationMembersSorting = ({
  sorting,
  setSorting,
  defaultSorting,
}: {
  defaultSorting: OrganisationMembershipsSorting[];
  sorting: OrganisationMembershipsSorting;
  setSorting: (value: OrganisationMembershipsSorting[]) => void;
}) => {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  const sortByOptions = React.useMemo(() => {
    const organisationMembersSortingLabels: Record<
      SortBy,
      Record<SortDirection, string>
    > = {
      firstname: {
        asc: t("Firstname asc", {
          ns: "OrganisationProfile",
        }),
        desc: t("Firstname desc", {
          ns: "OrganisationProfile",
        }),
      },
      familyname: {
        asc: t("Familyname asc", {
          ns: "OrganisationProfile",
        }),
        desc: t("Familyname desc", {
          ns: "OrganisationProfile",
        }),
      },
      organisationMembershipStatus: {
        asc: t("Status asc", {
          ns: "OrganisationProfile",
        }),
        desc: t("Status desc", {
          ns: "OrganisationProfile",
        }),
      },
      createdAt: {
        asc: t("Created at asc", {
          ns: "OrganisationProfile",
        }),
        desc: t("Created at desc", {
          ns: "OrganisationProfile",
        }),
      },
    };

    return ORGANISATION_MEMBERS_SORTING_FIELDS.map(({ column, direction }) => ({
      label: organisationMembersSortingLabels[column][direction],
      value: { column, direction },
    }));
  }, [t]);

  return (
    <Select
      label={t("Sort by", { ns: "Global" })}
      options={sortByOptions}
      value={
        sortByOptions.find(option => isEqual(option.value, sorting))?.value
      }
      onChange={value => {
        setSorting(value ? [value] : defaultSorting);
      }}
    />
  );
};
